import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from '@tanstack/react-router'
import {
  attachItem,
  detachRelation,
  serviceCatalogAttachableQuery,
  serviceCatalogCompositionQuery,
  type CompositionPage,
} from '../api/serviceCatalog'
import { moduleOf, shellQuery } from '../api/shell'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { CatalogHead, OriginPill } from './ServiceCatalogChrome'

/**
 * "What it rests on" - `Service_catalog::composition()`, macheta
 * `views/version-3/inventoree/service_catalog/composition.php`.
 *
 * Compunerea NU e o tabela a catalogului: sunt muchii obisnuite din
 * `it_ci_relations`, aceleasi pe care le desenaza harta si le urmeaza analiza
 * de impact. De-aia ce se leaga aici apare acolo fara alt pas, si de-aia
 * butoanele "On the map" si "What breaks with it" stau chiar in capul paginii.
 *
 * DOUA FELURI DE A CERE ACELASI ECRAN, ca in portal: pagina intreaga de la
 * meniu, si panoul din randul desfacut al listei (`inRow` in CI3). Panoul e
 * `CompositionPanel`, si e ACELASI cod - o a doua copie s-ar fi despartit de
 * prima la prima corectura facuta intr-una.
 */
export function ServiceCatalogCompositionPage() {
  const { id } = useParams({ from: '/service_catalog/composition/$id' })
  const entryId = Number(id)

  const page = useQuery(serviceCatalogCompositionQuery(entryId))
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(
    ['css/adm.css', 'css/service_shop.css', 'css/service_catalog.css'],
    shell.data?.version ?? '',
  )

  if (isForbidden(page.error)) return <Forbidden />
  if (!page.data) return <Spinner />

  return (
    <div id="content">
      <CompositionPanel page={page.data} entryId={entryId} />
    </div>
  )
}

/**
 * Ecranul cu tot cu capul lui: cifrele, drumurile si lista.
 *
 * Se deseneaza si intreg, si in randul desfacut al catalogului. In randul
 * desfacut PLEACA UN SINGUR BUTON - "The catalogue", care n-ar avea unde sa
 * duca fiindca lista e chiar dedesubt. Atat ascunde si macheta veche: in
 * `composition.php`, `if (empty($inRow))` sta NUMAI pe butonul ala, iar capul
 * de pagina se incarca dupa el, neconditionat.
 *
 * La mutare plecase tot capul, si cu el doua drumuri pe care omul le avea din
 * randul deschis si nu le mai avea de nicaieri - "On the map" si "What breaks
 * with it" - plus cifra care spune pe cate elemente se sprijina serviciul.
 */
export function CompositionPanel({ page, entryId, inRow = false }: {
  page: CompositionPage
  entryId: number
  inRow?: boolean
}) {
  const client = useQueryClient()
  const [chosen, setChosen] = useState('')
  const [search, setSearch] = useState('')

  const picker = useQuery({
    ...serviceCatalogAttachableQuery(entryId, search),
    enabled: page.canWrite,
  })

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['service-catalog'] })
  }

  const attach = useMutation({
    mutationFn: () => attachItem(entryId, Number(chosen)),
    onSuccess: (out) => {
      toast(out.message, out.status === 'OK' ? 'Saved' : 'Error', out.status === 'OK' ? 'success' : 'error')
      if (out.status === 'OK') {
        setChosen('')
        refresh()
      }
    },
  })

  const detach = useMutation({
    mutationFn: (relationId: number) => detachRelation(relationId),
    onSuccess: (out) => {
      toast(out.message, out.status === 'OK' ? 'Detached' : 'Error', out.status === 'OK' ? 'success' : 'error')
      if (out.status === 'OK') refresh()
    },
  })

  const { entry, composition } = page

  return (
    <>
      <CatalogHead
        title={entry.name}
        icon="fa-sitemap"
        tone="blue-mauve"
        note="what this service rests on — attach an item here and the map and the impact report have it immediately"
        actions={
          <>
            {/* Numai pe pagina intreaga: in randul deschis catalogul e chiar
                dedesubt, si un buton care spune "inapoi la catalog" n-are unde
                sa te duca. */}
            {!inRow && (
              <a className="btn btn-default btn-sm" href="/app/service_catalog">
                <i className="fas fa-arrow-left"></i> The catalogue
              </a>
            )}
            <a
              className="btn btn-default btn-sm"
              href={`/app/adm/map?root=${entry.ci_id}`}
              target="_blank"
              rel="noreferrer"
            >
              <i className="fas fa-project-diagram"></i> On the map
            </a>
            <a
              className="btn btn-default btn-sm"
              href={`/app/adm/impact/${entry.ci_id}`}
              target="_blank"
              rel="noreferrer"
            >
              <i className="fas fa-bolt"></i> What breaks with it
            </a>
          </>
        }
        stats={[
          {
            value: composition.length,
            label: 'items',
            tone: composition.length === 0 ? 'red-intense' : 'blue-mauve',
          },
          { value: entry.kind === 4 ? 'Business' : 'IT', label: 'service', tone: 'grey' },
        ]}
      />

      {page.canWrite && (
        <div className="cat-cards">
          <div className="adm-card adm-tone-blue-mauve">
            <div className="adm-card-head">
              <h5>
                <i className="fas fa-plus"></i> Attach something
              </h5>
              <span className="adm-card-note">
                an application, a server, a database — or another service, when this one rests on it
              </span>
            </div>

            <div className="adm-bar">
              <div className="adm-field adm-field-wide">
                <label htmlFor={`attachSearch-${entryId}`}>Item</label>
                {/*
                  Cautarea merge la SERVER, ca in portal: lista e taiata la 50 de
                  randuri dintr-un inventar de mii, deci filtrata in browser ar
                  cauta numai in fereastra deja adusa.
                */}
                <input
                  type="text"
                  className="form-control input-sm"
                  placeholder="Search by name"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ marginBottom: 6 }}
                />
                <select
                  className="form-control input-sm"
                  id={`attachSearch-${entryId}`}
                  value={chosen}
                  onChange={(e) => setChosen(e.target.value)}
                >
                  <option value="">{picker.data ? 'Choose an item' : 'Loading…'}</option>
                  {(picker.data?.items ?? []).map((one) => (
                    <option value={one.ID} key={one.ID}>
                      {one.name}
                      {one.class_name ? ` · ${one.class_name}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="adm-field">
                <label>&nbsp;</label>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={attach.isPending}
                  onClick={() => {
                    if (chosen === '') {
                      toast('Choose an item first.', 'Nothing chosen', 'error')
                      return
                    }
                    attach.mutate()
                  }}
                >
                  <i className="fas fa-link"></i> Attach
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {page.composition.length === 0 ? (
        <div className="adm-empty-inline">
          <i className="fas fa-unlink"></i>
          Nothing is attached yet. Until something is, the impact report cannot say what breaks when this service does,
          and the map draws it on its own.
        </div>
      ) : (
        <table className="table table-hover adm-list">
          <thead>
            <tr>
              <th>Item</th>
              <th>Class</th>
              <th>Relation</th>
              <th>Said by</th>
              <th>Last seen</th>
              <th className="text-right">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {page.composition.map((item) => (
              <tr key={item.relation_id}>
                <td>
                  {/* `/app/...`, nu `/cmdb/...`: portalul vechi nu mai e pe
                      server, deci adresa lui nu mai deschide nimic. Ruta din
                      v4 e aceeasi, sub basepath-ul aplicatiei. */}
                  <a href={`/app/cmdb/view/${item.ID}`} target="_blank" rel="noreferrer">
                    <strong>{item.name}</strong>
                  </a>
                </td>
                <td>{item.class_name}</td>
                <td className="adm-muted">{item.relation_name}</td>
                <td>
                  <OriginPill origin={item.origin} />
                </td>
                <td className="adm-muted adm-nowrap">{item.last_seen}</td>
                <td className="text-right adm-nowrap">
                  <a
                    className="btn btn-info btn-xs"
                    href={`/app/adm/impact/${item.ID}`}
                    title="What else rests on this item"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className="fas fa-bolt"></i>
                  </a>
                  {page.canWrite && (
                    <button
                      type="button"
                      className="btn btn-danger btn-xs"
                      title="Detach — the item itself is untouched"
                      disabled={detach.isPending}
                      onClick={() => {
                        if (
                          !window.confirm(
                            'Detach this item?\n\nThe item itself stays. Only the link between it and this service goes.',
                          )
                        ) {
                          return
                        }
                        detach.mutate(item.relation_id)
                      }}
                    >
                      <i className="fas fa-unlink"></i>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

/** Panoul cerut singur, pentru randul desfacut: aduce el datele. */
export function CompositionTab({ entryId }: { entryId: number }) {
  const page = useQuery(serviceCatalogCompositionQuery(entryId))

  if (isForbidden(page.error)) return <Forbidden />
  if (!page.data) return <Spinner />

  return <CompositionPanel page={page.data} entryId={entryId} inRow />
}
